import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { readErr } from '@/lib/api';
import { toast } from '@/lib/toast';
import { ShieldCheck } from 'lucide-react';

export default function AdminLogin() {
    const { login, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            const u = await login(email, password);
            if (!u.is_admin) {
                logout();
                toast.error('This account is not an admin.');
                return;
            }
            toast.success(`Welcome, ${u.name}`);
            navigate('/admin');
        } catch (err) {
            toast.error(readErr(err, 'Login failed'));
        }
    };

    return (
        <div className="relative min-h-screen flex items-center justify-center px-6 py-12 bg-obsidian">
            <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle at 50% 30%, rgba(245,158,11,0.15) 0%, transparent 50%)'
                }} />
            </div>
            <Card className="relative z-10 w-full max-w-md glass rounded-sm p-8 md:p-10 border-marigold/15" data-testid="admin-login-card">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-sm bg-marigold/15 border border-marigold/30 flex items-center justify-center">
                        <ShieldCheck size={20} className="text-marigold" />
                    </div>
                    <div>
                        <div className="font-serif text-2xl text-parchment leading-tight">Admin Console</div>
                        <div className="overline mt-0.5">AstroConsult internal</div>
                    </div>
                </div>
                <h1 className="font-serif text-3xl text-parchment">Sign in to admin.</h1>
                <p className="mt-2 text-sm text-slate-400">Restricted access. Operators only.</p>

                <form onSubmit={onSubmit} className="space-y-5 mt-8" data-testid="admin-login-form">
                    <div>
                        <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
                        <Input
                            id="email" type="email" required autoFocus
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            className="mt-2 bg-transparent border-b border-marigold/20 border-t-0 border-l-0 border-r-0 rounded-none px-0 focus-visible:ring-0 focus-visible:border-marigold text-parchment h-11"
                            data-testid="admin-login-email"
                        />
                    </div>
                    <div>
                        <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-wider">Password</Label>
                        <Input
                            id="password" type="password" required
                            value={password} onChange={(e) => setPassword(e.target.value)}
                            className="mt-2 bg-transparent border-b border-marigold/20 border-t-0 border-l-0 border-r-0 rounded-none px-0 focus-visible:ring-0 focus-visible:border-marigold text-parchment h-11"
                            data-testid="admin-login-password"
                        />
                    </div>
                    <Button type="submit" disabled={loading}
                        data-testid="admin-login-submit"
                        className="w-full bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 font-medium">
                        {loading ? 'Signing in…' : 'Enter admin'}
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                        <Link to="/" className="hover:text-parchment">Back to public site</Link>
                    </p>
                </form>
            </Card>
        </div>
    );
}
